import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Heart, LogOut, Copy, Plus, Users, Ticket, CalendarHeart, MapPin, Check, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LeafletMap } from "@/components/LeafletMap";
import { GeoSearch } from "@/components/GeoSearch";
import { DemoMembersPanel } from "@/components/DemoMembersPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export default function AdminDashboard() {
  const { logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailUser, setDetailUser] = useState(null);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [ev, setEv] = useState({ title: "", date: "", time: "", latitude: null, longitude: null, location_name: "", note: "" });

  const load = async () => {
    const [u, i, e] = await Promise.all([
      api.get("/admin/users"), api.get("/admin/invites"), api.get("/admin/match-events"),
    ]);
    setUsers(u.data); setInvites(i.data); setEvents(e.data);
  };
  useEffect(() => { load(); }, []);

  const genInvite = async () => {
    try {
      const { data } = await api.post("/admin/invites");
      toast.success(`Invite created: ${data.code}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const copyInvite = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Copied");
  };

  const revokeInvite = async (id) => {
    try {
      await api.delete(`/admin/invites/${id}`);
      toast.success("Invite revoked");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const removeMember = async () => {
    if (!memberToDelete) return;
    try {
      await api.delete(`/admin/users/${memberToDelete.id}`);
      toast.success(`${memberToDelete.name} removed`);
      setSelected((s) => s.filter((x) => x !== memberToDelete.id));
      setMemberToDelete(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };


  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const createEvent = async () => {
    if (selected.length < 1) return toast.error("Select at least one person");
    if (!ev.title || !ev.date || !ev.time || ev.latitude == null) return toast.error("Fill title, date, time and pick a point");
    try {
      await api.post("/admin/match-events", { ...ev, user_ids: selected });
      toast.success("Match event assigned");
      setDialogOpen(false); setSelected([]);
      setEv({ title: "", date: "", time: "", latitude: null, longitude: null, location_name: "", note: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const nameOf = (id) => users.find((u) => u.id === id)?.name || "Unknown";

  return (
    <div className="min-h-screen sunny-bg" data-testid="admin-page">
      <header className="border-b border-amber-100 px-6 py-4 flex items-center justify-between sticky top-0 bg-white/90 backdrop-blur-xl z-30">
        <div className="flex items-center gap-2">
          <Heart className="text-[#F43F5E] fill-[#F43F5E]" size={22} />
          <span className="font-display font-bold text-stone-900">Matchmaker Console</span>
        </div>
        <Button data-testid="admin-logout-btn" onClick={logout} variant="outline" size="sm" className="border-stone-300 gap-2 text-stone-700">
          <LogOut size={15} /> Sign out
        </Button>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <Tabs defaultValue="portfolio">
          <TabsList className="bg-white border border-amber-100 shadow-sm">
            <TabsTrigger value="portfolio" data-testid="tab-portfolio" className="gap-2"><Users size={15} /> Portfolio</TabsTrigger>
            <TabsTrigger value="invites" data-testid="tab-invites" className="gap-2"><Ticket size={15} /> Invites</TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events" className="gap-2"><CalendarHeart size={15} /> Match Events</TabsTrigger>
          </TabsList>

          {/* PORTFOLIO */}
          <TabsContent value="portfolio" className="mt-6">
            <DemoMembersPanel />
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="font-display font-bold text-2xl text-stone-900">All members <span className="text-stone-400">({users.length})</span></h2>
                <p className="text-stone-500 text-sm">{selected.length} selected</p>
              </div>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="assign-event-btn" disabled={selected.length === 0} className="bg-[#F43F5E] hover:bg-[#E11D48] text-white rounded-full gap-2 shadow-lg shadow-rose-500/25">
                    <Plus size={16} /> Assign match event
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white border-amber-100 max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle className="font-display">Assign to {selected.length} member(s)</DialogTitle></DialogHeader>
                  <div className="flex flex-wrap gap-2">
                    {selected.map((id) => <Badge key={id} className="bg-rose-100 text-[#E11D48] border-0">{nameOf(id)}</Badge>)}
                  </div>
                  <div className="space-y-3 mt-2">
                    <div>
                      <Label className="text-stone-600">Event title</Label>
                      <Input data-testid="event-title" value={ev.title} onChange={(e) => setEv({ ...ev, title: e.target.value })} className="mt-1.5 bg-white border-stone-200" placeholder="Wine tasting at dusk" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-stone-600">Date</Label>
                        <Input data-testid="event-date" type="date" value={ev.date} onChange={(e) => setEv({ ...ev, date: e.target.value })} className="mt-1.5 bg-white border-stone-200" />
                      </div>
                      <div>
                        <Label className="text-stone-600">Time</Label>
                        <Input data-testid="event-time" type="time" value={ev.time} onChange={(e) => setEv({ ...ev, time: e.target.value })} className="mt-1.5 bg-white border-stone-200" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-stone-600">Location name</Label>
                      <Input data-testid="event-location" value={ev.location_name} onChange={(e) => setEv({ ...ev, location_name: e.target.value })} className="mt-1.5 bg-white border-stone-200" placeholder="Rooftop Bar, SoHo" />
                    </div>
                    <div>
                      <Label className="text-stone-600 flex items-center gap-1.5"><MapPin size={14} /> Pick meeting point (NYC)</Label>
                      <div className="mt-1.5"><GeoSearch onPick={(la, ln, name) => setEv((p) => ({ ...p, latitude: la, longitude: ln, location_name: p.location_name || name || "" }))} /></div>
                      <div className="h-[220px] mt-2">
                        <LeafletMap lat={ev.latitude} lng={ev.longitude} onPick={(la, ln) => setEv((p) => ({ ...p, latitude: la, longitude: ln }))} />
                      </div>
                      {ev.latitude != null && <div className="text-xs text-stone-500 font-mono mt-1">{ev.latitude.toFixed(5)}, {ev.longitude.toFixed(5)}</div>}
                    </div>
                    <div>
                      <Label className="text-stone-600">Note (optional)</Label>
                      <Input data-testid="event-note" value={ev.note} onChange={(e) => setEv({ ...ev, note: e.target.value })} className="mt-1.5 bg-white border-stone-200" placeholder="Ask for the group under the name Cupid" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button data-testid="create-event-btn" onClick={createEvent} className="bg-[#F43F5E] hover:bg-[#E11D48] text-white rounded-full w-full">Send invitations</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {users.length === 0 ? (
              <p className="text-stone-400">No members yet. Generate invites to get started.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {users.map((u) => {
                  const isSel = selected.includes(u.id);
                  return (
                    <div key={u.id} data-testid={`user-card-${u.id}`}
                      className={`relative rounded-2xl border p-5 transition-colors cursor-pointer shadow-sm ${isSel ? "border-[#F43F5E] bg-rose-50" : "border-amber-100 bg-white hover:border-stone-300"}`}
                      onClick={() => toggleSelect(u.id)}>
                      <div className={`absolute top-4 right-4 w-5 h-5 rounded-md border flex items-center justify-center ${isSel ? "bg-[#F43F5E] border-[#F43F5E] text-white" : "border-stone-300"}`} data-testid={`select-user-${u.id}`}>
                        {isSel && <Check size={13} />}
                      </div>
                      <div className="flex items-center gap-3">
                        <Avatar className="w-14 h-14 border border-amber-100">
                          <AvatarImage src={mediaUrl(u.photo_url)} />
                          <AvatarFallback className="bg-amber-100 text-stone-700">{u.name?.[0] || "?"}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <div className="font-semibold text-stone-900 truncate">{u.name}</div>
                          <div className="text-sm text-stone-500 truncate">{u.occupation || "—"}{u.employer ? ` · ${u.employer}` : ""}</div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-4 text-xs">
                        {u.pref_gender && <Badge variant="outline" className="border-stone-300 text-stone-600">wants {u.pref_gender}</Badge>}
                        {u.looking_for && <Badge variant="outline" className="border-stone-300 text-stone-600">{u.looking_for}</Badge>}
                        {u.religion_important && <Badge variant="outline" className="border-stone-300 text-stone-600">religion: {u.religion || "yes"}</Badge>}
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <button data-testid={`view-user-${u.id}`} onClick={(e) => { e.stopPropagation(); setDetailUser(u); }}
                          className="text-xs text-[#E11D48] hover:underline">View full profile →</button>
                        <button data-testid={`remove-user-${u.id}`} onClick={(e) => { e.stopPropagation(); setMemberToDelete(u); }}
                          className="text-xs text-stone-400 hover:text-rose-500 inline-flex items-center gap-1 transition-colors">
                          <Trash2 size={13} /> Remove
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* INVITES */}
          <TabsContent value="invites" className="mt-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display font-bold text-2xl text-stone-900">Invite codes</h2>
              <Button data-testid="gen-invite-btn" onClick={genInvite} className="bg-[#F43F5E] hover:bg-[#E11D48] text-white rounded-full gap-2 shadow-lg shadow-rose-500/25"><Plus size={16} /> Generate code</Button>
            </div>
            <div className="space-y-2">
              {invites.length === 0 && <p className="text-stone-400">No invites yet.</p>}
              {invites.map((i) => (
                <div key={i.id} className="flex items-center justify-between bg-white border border-amber-100 rounded-xl px-5 py-3 shadow-sm" data-testid={`invite-${i.code}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-lg tracking-widest text-stone-900">{i.code}</span>
                    {i.used ? <Badge className="bg-stone-100 text-stone-500 border-0">used by {i.used_by}</Badge>
                      : <Badge className="bg-emerald-100 text-emerald-600 border-0">available</Badge>}
                  </div>
                  {!i.used && (
                    <div className="flex items-center gap-1">
                      <Button data-testid={`copy-invite-${i.code}`} onClick={() => copyInvite(i.code)} variant="ghost" size="sm" className="gap-2 text-stone-500"><Copy size={14} /> Copy</Button>
                      <Button data-testid={`revoke-invite-${i.code}`} onClick={() => revokeInvite(i.id)} variant="ghost" size="sm" className="gap-2 text-rose-500 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={14} /> Revoke</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          {/* EVENTS */}
          <TabsContent value="events" className="mt-6">
            <h2 className="font-display font-bold text-2xl mb-5 text-stone-900">Assigned match events</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {events.length === 0 && <p className="text-stone-400">No events assigned yet.</p>}
              {events.map((e) => (
                <div key={e.id} className="bg-white border border-amber-100 rounded-2xl p-5 shadow-sm" data-testid={`event-${e.id}`}>
                  <div className="font-display font-bold text-lg text-stone-900">{e.title}</div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-stone-600 mt-2">
                    <span>📅 {e.date}</span><span>⏰ {e.time}</span><span>📍 {e.location_name}</span>
                  </div>
                  <div className="text-xs text-stone-400 font-mono mt-1">{Number(e.latitude).toFixed(4)}, {Number(e.longitude).toFixed(4)}</div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {e.user_ids.map((id) => <Badge key={id} className="bg-rose-100 text-[#E11D48] border-0">{nameOf(id)}</Badge>)}
                  </div>
                  {e.note && <p className="text-sm text-stone-500 mt-3">{e.note}</p>}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* User detail dialog */}
      <Dialog open={!!detailUser} onOpenChange={(o) => !o && setDetailUser(null)}>
        <DialogContent className="bg-white border-amber-100">
          {detailUser && (
            <>
              <DialogHeader><DialogTitle className="font-display">{detailUser.name}</DialogTitle></DialogHeader>
              <div className="flex items-center gap-4">
                <Avatar className="w-20 h-20 border border-amber-100">
                  <AvatarImage src={mediaUrl(detailUser.photo_url)} />
                  <AvatarFallback className="bg-amber-100 text-2xl text-stone-700">{detailUser.name?.[0]}</AvatarFallback>
                </Avatar>
                <div className="text-sm text-stone-600 space-y-1">
                  <div>{detailUser.occupation} {detailUser.employer && `at ${detailUser.employer}`}</div>
                  <div>{detailUser.email}</div>
                  <div className="font-mono text-stone-500">{detailUser.latitude ? `${Number(detailUser.latitude).toFixed(4)}, ${Number(detailUser.longitude).toFixed(4)}` : "no geo point"}</div>
                </div>
              </div>
              <div className="text-sm text-stone-600 space-y-2 mt-2">
                <div><span className="text-stone-400">Interested in:</span> {detailUser.pref_gender || "—"}</div>
                <div><span className="text-stone-400">Looking for:</span> {detailUser.looking_for || "—"}</div>
                <div><span className="text-stone-400">Age:</span> {detailUser.age || "—"}</div>
                <div><span className="text-stone-400">Wants ages:</span> {detailUser.match_age_min != null ? `${detailUser.match_age_min} – ${detailUser.match_age_max}` : "—"}</div>
                <div><span className="text-stone-400">About:</span> {detailUser.about || "—"}</div>
                <div><span className="text-stone-400">Neighborhood activities:</span> {detailUser.likes || "—"}</div>
                <div><span className="text-stone-400">Wants in a partner:</span> {detailUser.partner_prefs || "—"}</div>
                <div><span className="text-stone-400">Dislikes:</span> {detailUser.dislikes || "—"}</div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Remove member confirmation */}
      <AlertDialog open={!!memberToDelete} onOpenChange={(o) => !o && setMemberToDelete(null)}>
        <AlertDialogContent className="bg-white border-amber-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Remove {memberToDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription className="text-stone-500">
              This permanently deletes their profile and removes them from any assigned match events. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-remove-btn" className="border-stone-300">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-remove-btn" onClick={removeMember} className="bg-[#F43F5E] hover:bg-[#E11D48] text-white">Remove member</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
